/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Navigation Menu Configuration & Route Definitions (Task 10.1.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import {
  LayoutDashboard,
  FileText,
  Sparkles,
  Wand2,
  MessageSquare,
  Bot,
  Database,
  Image as ImageIcon,
  Palette,
  Video,
  UserCheck,
  Mic,
  Volume2,
  AudioWaveform,
  Sliders,
  Languages,
  BookOpen,
  SpellCheck,
  Key,
  Webhook,
  Code2,
  CreditCard,
  Wallet,
  Settings,
  Images,
  type LucideIcon,
} from 'lucide-react';
import type { SophiaDefaultIconName } from '@sofia/assets';

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  sophiaIcon?: SophiaDefaultIconName;
  badge?: 'New' | 'Beta' | string;
  badgeVariant?: 'primary' | 'accent' | 'emerald';
  description?: string;
  isExternal?: boolean;
}

export interface NavigationSection {
  id: string;
  title: string;
  sophiaIcon?: SophiaDefaultIconName;
  items: NavigationItem[];
}

export const NAVIGATION_SECTIONS: NavigationSection[] = [
  {
    id: 'overview',
    title: 'Workspace',
    sophiaIcon: 'hologram',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/workspace/dashboard',
        icon: LayoutDashboard,
        sophiaIcon: 'hologram',
        description: 'Workspace overview, credits & activity feed',
      },
    ],
  },
  {
    id: 'writer',
    title: 'AI Writer & Documents',
    sophiaIcon: 'algorithm',
    items: [
      {
        id: 'documents',
        label: 'Documents',
        path: '/workspace/documents',
        icon: FileText,
        sophiaIcon: 'brainstorm',
        description: 'Manage rich-text documents and folders',
      },
      {
        id: 'writer-templates',
        label: 'AI Writer & Templates',
        path: '/workspace/writer',
        icon: Sparkles,
        sophiaIcon: 'artificial-intelligence',
        description: '35+ multilingual AI templates',
      },
      {
        id: 'article-wizard',
        label: 'Article Wizard',
        path: '/workspace/wizard',
        icon: Wand2,
        sophiaIcon: 'algorithm',
        badge: 'Beta',
        badgeVariant: 'accent',
        description: '4-step longform SEO article pipeline',
      },
    ],
  },
  {
    id: 'chat',
    title: 'AI Chat & Assistants',
    sophiaIcon: 'chat-bot',
    items: [
      {
        id: 'chat-studio',
        label: 'Chat Studio',
        path: '/workspace/chat',
        icon: MessageSquare,
        sophiaIcon: 'chat-bot',
        description: 'Multi-modal AI chat sessions',
      },
      {
        id: 'assistants',
        label: 'Specialist Bots',
        path: '/workspace/assistants',
        icon: Bot,
        sophiaIcon: 'mind',
        description: 'Pre-configured domain expert personas',
      },
      {
        id: 'rag-knowledge',
        label: 'Knowledge Base (RAG)',
        path: '/workspace/rag',
        icon: Database,
        sophiaIcon: 'big-data',
        badge: 'New',
        badgeVariant: 'primary',
        description: 'Vectorize document embeddings and search',
      },
      {
        id: 'chatbot-widgets',
        label: 'Chatbot Widgets',
        path: '/workspace/widgets',
        icon: Code2,
        sophiaIcon: 'robot',
        badge: 'New',
        badgeVariant: 'accent',
        description: 'Embeddable website chatbot widget studio',
      },
    ],
  },
  {
    id: 'media',
    title: 'AI Media Studio',
    sophiaIcon: 'vision',
    items: [
      {
        id: 'image-generation',
        label: 'Image Studio',
        path: '/workspace/media/images',
        icon: ImageIcon,
        sophiaIcon: 'vision',
        description: 'Flux Pro and DALL-E 3 text-to-image',
      },
      {
        id: 'inpainting-canvas',
        label: 'Inpainting Canvas',
        path: '/workspace/media/canvas',
        icon: Palette,
        sophiaIcon: 'eye-recognition',
        description: 'Mask editing and smart replacement',
      },
      {
        id: 'image-ops',
        label: '1-Click Image Ops',
        path: '/workspace/media/ops',
        icon: Wand2,
        sophiaIcon: 'face-recognition',
        badge: '4K AI',
        badgeVariant: 'primary',
        description: 'Super-resolution 4x and background removal',
      },
      {
        id: 'video-avatar',
        label: 'Cinematic Video',
        path: '/workspace/media/video',
        icon: Video,
        sophiaIcon: 'camera-360',
        badge: 'New',
        badgeVariant: 'emerald',
        description: 'Luma, Kling and Runway cinematic video synthesis',
      },
      {
        id: 'talking-avatar',
        label: 'Talking Avatar',
        path: '/workspace/media/avatar',
        icon: UserCheck,
        sophiaIcon: 'vr-glasses',
        badge: 'Lip-Sync',
        badgeVariant: 'accent',
        description: 'Synthesia talking head avatars and lip-sync',
      },
      {
        id: 'media-gallery',
        label: 'Media Gallery',
        path: '/workspace/gallery',
        icon: Images,
        sophiaIcon: 'solution',
        badge: 'Vault',
        badgeVariant: 'primary',
        description: 'Centralized digital asset manager across images, videos and audio',
      },
    ],
  },
  {
    id: 'voice',
    title: 'AI Voice Studio',
    sophiaIcon: 'voice-recognition',
    items: [
      {
        id: 'voice-catalog',
        label: 'Voice Catalog',
        path: '/workspace/voice/catalog',
        icon: AudioWaveform,
        sophiaIcon: 'voice-recognition',
        badge: '40+ Voices',
        badgeVariant: 'primary',
        description: 'Multi-provider voice catalog and instant audition',
      },
      {
        id: 'tts-generator',
        label: 'Text to Speech',
        path: '/workspace/voice/tts',
        icon: Volume2,
        sophiaIcon: 'virtual-call',
        description: 'Natural neural voices across 4 providers',
      },
      {
        id: 'voice-cloning',
        label: 'Voice Cloning',
        path: '/workspace/voice/cloning',
        icon: Mic,
        sophiaIcon: 'biofuel',
        description: 'Clone custom voices with instant preview',
      },
      {
        id: 'whisper-transcribe',
        label: 'Whisper STT',
        path: '/workspace/voice/transcribe',
        icon: AudioWaveform,
        sophiaIcon: 'science',
        badge: 'Subtitles',
        badgeVariant: 'emerald',
        description: 'High-accuracy audio transcription & waveform subtitle studio',
      },
      {
        id: 'audio-mixer',
        label: 'Web Audio Mixer',
        path: '/workspace/voice/mixer',
        icon: Sliders,
        sophiaIcon: 'gamepad',
        badge: 'DAW AI',
        badgeVariant: 'primary',
        description: 'Multi-track DAW with smart audio ducking',
      },
    ],
  },
  {
    id: 'translation',
    title: 'CAT Translation Studio',
    sophiaIcon: 'nano-tech',
    items: [
      {
        id: 'cat-editor',
        label: 'Translation Editor',
        path: '/workspace/translation',
        icon: Languages,
        sophiaIcon: 'nano-tech',
        description: 'Bilingual CAT segmentation editor',
      },
      {
        id: 'translation-memory',
        label: 'Translation Memory',
        path: '/workspace/translation/tm',
        icon: BookOpen,
        sophiaIcon: 'layer',
        description: 'Reusable TM segments with fuzzy match',
      },
      {
        id: 'glossary',
        label: 'Terminology Glossary',
        path: '/workspace/translation/glossary',
        icon: SpellCheck,
        sophiaIcon: 'smart-search',
        description: 'Domain-specific brand terminology',
      },
    ],
  },
  {
    id: 'developer',
    title: 'Developer API',
    sophiaIcon: 'binary-code',
    items: [
      {
        id: 'api-keys',
        label: 'API Keys',
        path: '/workspace/developer/keys',
        icon: Key,
        sophiaIcon: 'finger-scanner',
        badge: 'v1',
        badgeVariant: 'primary',
        description: 'Manage secret keys (sk_live_...)',
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        path: '/workspace/developer/webhooks',
        icon: Webhook,
        sophiaIcon: 'robotic-arm',
        description: 'Configure real-time event endpoints',
      },
      {
        id: 'api-docs',
        label: 'API Reference',
        path: '/docs',
        icon: Code2,
        sophiaIcon: 'binary-code',
        isExternal: true,
        description: 'Interactive OpenAPI Scalar documentation',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Billing & Settings',
    sophiaIcon: 'digital-wallet',
    items: [
      {
        id: 'wallet-credits',
        label: 'Wallet & Credits',
        path: '/workspace/billing/wallet',
        icon: Wallet,
        sophiaIcon: 'battery',
        badge: 'Ledger',
        badgeVariant: 'emerald',
        description: 'Real-time D1 ledger balance and history',
      },
      {
        id: 'plans-billing',
        label: 'Subscription Plans',
        path: '/workspace/billing/plans',
        icon: CreditCard,
        sophiaIcon: 'digital-wallet',
        badge: 'Pro',
        badgeVariant: 'accent',
        description: 'Upgrade tiers and token packs',
      },
      {
        id: 'workspace-settings',
        label: 'Workspace Settings',
        path: '/workspace/settings',
        icon: Settings,
        sophiaIcon: 'smart-door',
        description: 'Manage members, permissions and brand voice',
      },
    ],
  },
];
